import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import BackButton from "../BackButton/BackButton";
import { getNotificationList } from "../../state/actions/notificationListAction";
import { useDispatch, useSelector } from "react-redux";
import { useAuth } from "../../features/auth/AuthContext";
import { resetNotificationList } from '../../state/reducers/notificationListReducer';
import { CircularProgress, Grid } from "@mui/material";
import api from "../../api/api";
import { convertTo12Hour } from "../../components/BookAMeeting/meetingUtils";
import "./NotificationPage.css";


const NotificationPage = () => {
  const { token } = useAuth();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { notifications, isLoading  } = useSelector((state) => state.notificationList);
  const globalState = useSelector((state) => state.globalState);
  const isMarketplace = globalState.enabled_modules.includes("marketplace");
  const [localNotifications, setLocalNotifications] = useState([]);
  const isTeacher = globalState.role === "Teacher";
  // Moved this useEffect to the top level
  useEffect(() => {
    dispatch(getNotificationList(token));
    return () => {
      dispatch(resetNotificationList());
    };
  }, [dispatch, token]); // Added dependencies to the dependency array

  // Update local notifications when Redux state changes
  useEffect(() => {
    if (notifications) {
      setLocalNotifications(notifications);
    }
  }, [notifications]);

  const formatTimestamp = (isoTimestamp) => {
    const fixedDateTime = new Date(isoTimestamp);
    const readableFormat = fixedDateTime.toLocaleString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: false,
    }) + ', ' + fixedDateTime.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    return readableFormat;
  }

  const convertNotificationTimeTo12Hour = (message) => {
    // Match time patterns like "11:30" or "14:45" and convert to 12-hour format using existing utility
    return message.replace(/\b(\d{1,2}):(\d{2})\b/g, (match) => {
      return convertTo12Hour(match);
    });
  }

  const readNotification = async (notification) => {
    const id = notification.id;
    try {
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Token ${token}`,
      };
      const payload = { notification_id: id };
      const response = await api.post(
        `notifications/notifications_list/`,
        payload,
        { headers }
      );

      if (response.status === 200 || response.status === 201) {
        // Remove notification from the local list
        setLocalNotifications(prev => prev.filter((item) => item.id !== id));

        // Check if it's a meeting notification based on category
        if (notification.category === "meeting") {
          // Helper function to parse date from notification message
          const parseDateFromMessage = (msg) => {
            // Match patterns like "04 Sep 2026" or "Sep 04 2026"
            const dateMatch = msg.match(/(\d{1,2})\s+(\w+)\s+(\d{4})/);
            if (dateMatch) {
              try {
                return new Date(`${dateMatch[1]} ${dateMatch[2]} ${dateMatch[3]}`);
              } catch (e) {
                return null;
              }
            }
            return null;
          };

          // Determine if meeting is in history (cancelled/completed/past date) or upcoming
          const msg = notification.notification_message?.toLowerCase() || "";
          const meetingDate = parseDateFromMessage(notification.notification_message || "");
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          const isPastDate = meetingDate && meetingDate < today;
          const isCancelledOrCompleted =
            msg.includes("cancel") ||
            msg.includes("completed") ||
            msg.includes("finish") ||
            notification.is_accepted === false ||
            notification.status === 2 || // completed
            notification.status === 3 || // cancelled
            notification.booking_status === "cancelled" ||
            notification.booking_status === "completed" ||
            isPastDate;

          const activeTab = isCancelledOrCompleted ? "history" : "upcoming";
          console.log("Meeting notification:", { notification, isCancelledOrCompleted, activeTab, meetingDate, isPastDate });
          navigate("/dashboard/meetings", {
            replace: true,
            state: {
              activeTab: activeTab,
              highlightMeetingId: notification.booking_id || notification.meeting_id || notification.id
            }
          });
        } else if (notification.course_id) {
          if(!isMarketplace){
             const targetPath = isTeacher
            ? `/course/${notification.course_id}/view`
            : notification.is_accepted
            ? `/course/${notification.course_id}`
            : `/course/${notification.course_id}/view`;

          navigate(targetPath, {
            state: {
              course_id: notification.course_id,
              from: "notification",
              course: { course_id: notification.course_id },
            },
            replace: false,
          });
          }else {
              const targetPath = isTeacher
            ? `/course/${notification.course_id}/view`
            : notification.is_accepted
            ? `/course/${notification.course_id}`
            : `/course/${notification.course_id}/view`;

          navigate(targetPath, {
            state: {
              course_id: notification.course_id,
              from: "notification",
              course: { course_id: notification.course_id },
            },
            replace: false,
          });
          }
        
        } else {
          let alternativePath = null;

          if (notification.notification_type) {
            switch (notification.notification_type) {
              case "assignment":
              case "lesson":
                if (notification.lesson_id) {
                  alternativePath = `/lesson/${notification.lesson_id}`;
                }
                break;
              case "message":
              case "chat":
                alternativePath = "/dashboard/messages";
                break;
              case "profile":
              case "account":
                alternativePath = "/settings";
                break;
              case "withdrawal":
              alternativePath ="/revenue-details";
              break;
               case "purchase":
              alternativePath =`/course/${notification.course_id}`;
              default:
            }
          }

          if (notification.target_url) {
            alternativePath = notification.target_url;
          }

          if (alternativePath) {
            navigate(alternativePath, { replace: true });
          } else {
            navigate("/dashboard/home", { replace: true });
          }
        }
      } else {
        console.error("Fetch error:", response);
      }
    } catch (error) {
      console.error("Error:", error);
    }
  }; // End of readNotification function

  return (
    <>
      <div id="NotificationPage-section">
        <div className="NotificationPage-wrapper">
          <div className="container-fluid">
            <div className="row">
              <div className="col-lg-12 col-md-12">
                <div className="view-course-Details-back-btn-folder">
                  <BackButton fallbackPath="/"  buttonName={"Notifications"} />
                </div>

                {isLoading  ? (
                  <Grid
                    height={"50vh"}
                    xs={12}
                    container
                    justifyContent={"center"}
                    alignItems={"center"}
                  >
                    <CircularProgress size={80} />
                  </Grid>
                ) : localNotifications?.length > 0 ? (
                  localNotifications.map((notification, i) => (
                    <div className="notifications-all-container" key={i}>
                      <div
                        className="notificationPage-folder"
                        onClick={() => readNotification(notification)}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="notifications-folder">
                          <div className="notifications-inner-folder">
                            <div className="notifications-image">
                              {notification.profile_pic ? (
                                <img
                                  src={notification.profile_pic}
                                  alt="Profile Pic"
                                />
                              ) : (
                                <div className="defult-proifle-pic">
                                  <i className="fa-solid fa-user"></i>
                                </div>
                              )}
                            </div>
                            <p>{convertNotificationTimeTo12Hour(notification.notification_message)}</p>
                          </div>
                          <div className="notification-time-folder">
                            <span>{formatTimestamp(notification.created_at)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-notifications">
                    <p>No Notifications Found</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default NotificationPage;